/**
 * Shared front-end JS for both Villa Amber and Naran Hotell.
 *
 * Both themes use the same `data-va-*` data-attribute hooks (the `va` prefix
 * is the platform prefix per the decisions doc, not a brand prefix). Each
 * theme's entry file (`app.ts` / `naran-app.ts`) imports its own CSS bundle
 * and then imports this module to wire up the JS.
 *
 * Per OhMy patterns (memory/reference_ohmy_patterns.md):
 *   - Critical, above-the-fold JS goes here directly.
 *   - Anything non-critical is lazy-loaded via dynamic import inside an
 *     `requestIdleCallback` wrapper (with `setTimeout` fallback) so it never
 *     blocks the first paint.
 *   - No Vue for page content — vanilla TS only.
 */

const idle = (cb: () => void): void => {
    if ("requestIdleCallback" in window) {
        (
            window as unknown as {
                requestIdleCallback: (cb: () => void) => void;
            }
        ).requestIdleCallback(cb);
    } else {
        window.setTimeout(cb, 200);
    }
};

/**
 * Sticky availability bar — hidden while the visitor is looking at the hero,
 * revealed once they've scrolled past it. Bar sits inside the sticky header
 * so showing it makes the header taller, which is the intended behaviour.
 *
 * Why we don't use IntersectionObserver here: the bar is inside the sticky
 * header and the hero is in the flow below it. Showing the bar grows the
 * header and pushes the hero down inside the viewport. An observer keyed on
 * the hero would then re-fire "hero visible", hide the bar, shrink the
 * header, hero pops back up, fire again — a flicker loop.
 *
 * Instead we cache the hero's natural document-space bottom position once
 * at init (while the bar is still hidden) and compare `scrollY` against it.
 * scrollY does not change when the header resizes, so the threshold check
 * is stable no matter how the layout shifts on toggle. The measurement is
 * refreshed on resize, temporarily hiding the bar so we don't capture an
 * inflated position.
 */
const initAvailBar = (): void => {
    const hero = document.querySelector<HTMLElement>("[data-va-hero]");
    const avail = document.querySelector<HTMLElement>("[data-va-avail-bar]");
    if (!avail) return;
    if (!hero) {
        avail.removeAttribute("hidden");
        return;
    }

    let showThreshold = 0;
    const measure = (): void => {
        const rect = hero.getBoundingClientRect();
        showThreshold = rect.bottom + window.scrollY - window.innerHeight * 0.2;
    };
    measure();

    let ticking = false;
    const update = (): void => {
        ticking = false;
        const shouldShow = window.scrollY > showThreshold;
        if (shouldShow) {
            avail.removeAttribute("hidden");
        } else {
            avail.setAttribute("hidden", "");
        }
    };
    const onScroll = (): void => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
    };
    const onResize = (): void => {
        // Hide the bar for the measurement so we capture the hero's natural
        // position, not one inflated by an already-visible bar.
        avail.setAttribute("hidden", "");
        measure();
        update();
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize, { passive: true });
    update();
};

/**
 * Mobile drawer menu.
 *
 * The hamburger button toggles the drawer; clicking the backdrop, pressing
 * Escape, or tapping a nav link closes it. The slide-in transform lives on
 * the panel element so the backdrop can fade independently.
 */
const initMobileMenu = (): void => {
    const root = document.querySelector<HTMLElement>("[data-va-menu]");
    const toggle = document.querySelector<HTMLButtonElement>("[data-va-menu-toggle]");
    if (!root || !toggle) return;

    const panel = root.querySelector<HTMLElement>("[data-va-menu-panel]");
    const backdrop = root.querySelector<HTMLElement>("[data-va-menu-backdrop]");
    const closers = root.querySelectorAll<HTMLElement>(
        "[data-va-menu-close], [data-va-menu-link]"
    );

    const open = (): void => {
        root.classList.remove("hidden");
        root.setAttribute("aria-hidden", "false");
        toggle.setAttribute("aria-expanded", "true");
        document.body.style.overflow = "hidden";
        requestAnimationFrame(() => {
            panel?.classList.remove("translate-x-full");
        });
    };

    const close = (): void => {
        toggle.setAttribute("aria-expanded", "false");
        root.setAttribute("aria-hidden", "true");
        panel?.classList.add("translate-x-full");
        document.body.style.overflow = "";
        window.setTimeout(() => {
            root.classList.add("hidden");
        }, 280);
    };

    toggle.addEventListener("click", open);
    backdrop?.addEventListener("click", close);
    closers.forEach(el => el.addEventListener("click", close));
    document.addEventListener("keydown", e => {
        if (e.key === "Escape" && root.getAttribute("aria-hidden") === "false") {
            close();
        }
    });
};

/* ──────── Critical (run synchronously) ──────── */
initAvailBar();
initMobileMenu();

/* ──────── Lazy modules (loaded only if their hook is on the page) ──────── */
idle(() => {
    if (document.querySelector("[data-va-swipe-rail]")) {
        import("./modules/swipe-rail")
            .then(m => m.initSwipeRails())
            .catch(() => {});
    }
    if (document.querySelector("[data-va-voices]")) {
        import("./modules/voices").then(m => m.initVoices()).catch(() => {});
    }
    if (document.querySelector("[data-va-hero-rotation]")) {
        import("./modules/hero-rotation")
            .then(m => m.initHeroRotation())
            .catch(() => {});
    }
});
